import {
	type API_ListSupportTicketsQuery,
	DEFAULT_LIMIT,
	type SupportTicketStatus,
	type SupportTicketType,
} from "@pilatestime/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Calendar, Eye, Plus, Ticket } from "lucide-react";
import { useTranslation } from "react-i18next";
import { fetchUserSupportTicketsQueryOptions } from "@/api/supportTickets";
import { Button } from "@/components/ui/button";
import { useApiParameters } from "@/hooks/useApiParameters";
import { useDateFormatter } from "@/hooks/useDateFormatter";

export const Route = createFileRoute("/_auth/app/_app/help/support/tickets/")({
	loader: async ({ context }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		if (!token) throw new Error("No authentication token");

		const query: API_ListSupportTicketsQuery = {
			page: 1,
			limit: DEFAULT_LIMIT,
		};

		context.queryClient.ensureQueryData(
			fetchUserSupportTicketsQueryOptions(userId, token, query),
		);

		return {
			userId,
			token,
			query,
		};
	},
	component: SupportTicketsPage,
});

function SupportTicketsPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { formatDate } = useDateFormatter();
	const { userId, token, query } = Route.useLoaderData();

	// Fetch tickets data
	const { data: response } = useSuspenseQuery(
		fetchUserSupportTicketsQueryOptions(userId, token, query),
	);
	const tickets = response.data;

	// Status badge component
	const getStatusBadge = (status: SupportTicketStatus) => {
		const statusConfig = {
			open: { color: "bg-blue-100 text-blue-800", label: t("help.statusOpen") },
			waiting_for_user: {
				color: "bg-yellow-100 text-yellow-800",
				label: t("help.statusWaitingForUser"),
			},
			waiting_for_staff: {
				color: "bg-orange-100 text-orange-800",
				label: t("help.statusWaitingForStaff"),
			},
			resolved: {
				color: "bg-green-100 text-green-800",
				label: t("help.statusResolved"),
			},
			closed: {
				color: "bg-gray-100 text-gray-800",
				label: t("help.statusClosed"),
			},
		};

		const config = statusConfig[status];
		return (
			<span
				className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}
			>
				{config.label}
			</span>
		);
	};

	// Type badge component
	const getTypeBadge = (type: SupportTicketType) => {
		const typeConfig = {
			request: {
				color: "bg-blue-100 text-blue-800",
				label: t("help.supportTypeRequest"),
			},
			suggestion: {
				color: "bg-purple-100 text-purple-800",
				label: t("help.supportTypeSuggestion"),
			},
			complaint: {
				color: "bg-red-100 text-red-800",
				label: t("help.supportTypeComplaint"),
			},
		};

		const config = typeConfig[type];
		return (
			<span
				className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}
			>
				{config.label}
			</span>
		);
	};

	return (
		<div className="h-full flex flex-col overflow-hidden">
			{/* Content */}
			<div className="flex-1 min-h-0 p-6">
				{tickets.length === 0 ? (
					// Empty state
					<div className="h-full flex items-center justify-center">
						<div className="text-center py-8">
							<div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
								<Ticket className="h-8 w-8 text-primary" />
							</div>
							<h3 className="text-lg font-medium text-base-content mb-2">
								{t("help.noTickets")}
							</h3>
							<p className="text-base-content/70 mb-4">
								{t("help.noTicketsDescription")}
							</p>
							<Link to="/app/help/support" className="btn btn-primary">
								<Plus className="h-4 w-4" />
								{t("help.submitTicket")}
							</Link>
						</div>
					</div>
				) : (
					// Table
					<div className="h-full overflow-hidden">
						<div className="h-full overflow-x-auto overflow-y-auto">
							<table className="table table-pin-rows w-full min-w-max">
								<thead>
									<tr>
										<th className="bg-primary/3 whitespace-nowrap text-left">
											{t("help.supportFieldTitle")}
										</th>
										<th className="bg-primary/3 whitespace-nowrap text-left">
											{t("help.supportFieldType")}
										</th>
										<th className="bg-primary/3 whitespace-nowrap text-left">
											{t("common.status")}
										</th>
										<th className="bg-primary/3 whitespace-nowrap text-left">
											{t("common.createdAt")}
										</th>
										<th className="bg-primary/3 whitespace-nowrap text-left">
											{t("common.actions")}
										</th>
									</tr>
								</thead>
								<tbody>
									{tickets.map((ticket) => (
										<tr key={ticket.id} className="hover">
											<td className="whitespace-nowrap">
												<div className="flex items-center space-x-3">
													<div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
														<Ticket className="h-4 w-4 text-primary" />
													</div>
													<div className="min-w-0 flex-1">
														<div className="font-medium text-base-content truncate">
															{ticket.title}
														</div>
														<div className="text-sm text-base-content/60 truncate">
															#{ticket.id.slice(-8)}
														</div>
													</div>
												</div>
											</td>
											<td className="whitespace-nowrap">
												{getTypeBadge(ticket.type)}
											</td>
											<td className="whitespace-nowrap">
												{getStatusBadge(ticket.status)}
											</td>
											<td className="whitespace-nowrap">
												<div className="flex items-center space-x-2">
													<Calendar className="h-4 w-4 text-base-content/60" />
													<span className="text-sm">
														{formatDate(new Date(ticket.createdAt).getTime())}
													</span>
												</div>
											</td>
											<td className="whitespace-nowrap">
												<Button
													variant="ghost"
													size="sm"
													onClick={(e) => {
														e.stopPropagation();
														navigate({
															to: "/app/help/support/tickets/$ticketId",
															params: { ticketId: ticket.id },
														});
													}}
													className="text-primary hover:text-primary"
												>
													<Eye className="w-4 h-4" />
												</Button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
